import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SearchResult } from "@/models/search/SearchResults";
import { useConversationHub } from "@/hooks/useConversationHub";
import { buildSearchUrlParams } from "@/utils/searchParamsFormatter";
import useSearch from "@/hooks/useSearch";
import useConsolidationRenderingEnabled from "@/hooks/useConsolidationRenderingEnabled";
import { useGlobalSearchContext } from "@/providers/global-search/GlobalSearchContext";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import {
  openAppNavigation,
  shouldOpenInNewTab,
  type AppNavigationTarget,
} from "@/lib/navigation/appNavigation";
import { appendDocSearchParamToPath } from "@/partials/document-renderer/utils/referenceHighlightQuery";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import { resolveSearchLegislationOpenNavigationTarget } from "@/lib/consolidationLegislationUtils";

/**
 * Carries the global search term into the opened document so its viewer marks
 * and scrolls to the match (LAR-264). PDF viewers cannot mark text, so they are
 * left untouched.
 */
const withDocSearch = (
  target: AppNavigationTarget,
  docSearch: string | undefined
): AppNavigationTarget => {
  if (!docSearch) {
    return target;
  }
  if (target.kind === "legislation" || target.kind === "workspaceDocument") {
    return { ...target, docSearch };
  }
  if (target.kind === "path") {
    return {
      ...target,
      path: appendDocSearchParamToPath(target.path, docSearch),
    };
  }
  return target;
};

export const useSearchResultActions = () => {
  const navigate = useNavigate();
  const { selectConversationById } = useConversationHub();
  const { createSearch } = useSearch();
  const { searchQuery, setIsSearchbarOpen } = useGlobalSearchContext();
  const { resetWorkspaceDocumentVersionStore } =
    useWorkspaceDocumentVersionStore();
  const { enabled: consolidationFeatureEnabled } =
    useConsolidationRenderingEnabled();

  const scrollToConversation = (chatId: string) => {
    const maxRetries = 3;
    const delay = 300;

    const attemptScroll = (retryCount = 0) => {
      const conversationElement = document.getElementById(
        `conversation-${chatId}`
      );

      if (conversationElement) {
        conversationElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (retryCount < maxRetries) {
        setTimeout(() => attemptScroll(retryCount + 1), delay);
      }
    };

    attemptScroll();
  };

  const handleCreateSearch = useCallback(async () => {
    const params = buildSearchUrlParams(searchQuery, searchQuery?.searchTerm);
    await createSearch.mutateAsync({
      searchUrl: `/search?${params.toString()}`,
    });
  }, [searchQuery, createSearch]);

  const handleResultClick = useCallback(
    async (result: SearchResult, event?: React.MouseEvent) => {
      setIsSearchbarOpen(false);
      /**
       * Same combination the search itself uses (GlobalSearchProvider's
       * buildSearchTerm): tags carry the same advanced syntax as the term, so a
       * tag-only search still highlights in the opened document.
       */
      const docSearch =
        [searchQuery?.searchTerm ?? "", ...(searchQuery?.searchTags ?? [])]
          .join(" ")
          .trim() || undefined;
      if (result.legislation) {
        const legislation = result.legislation;
        if (!legislation.source) {
          logger.warn("Legislation search result has no source", {
            operation: "openSearchResult",
            legislationId: legislation.id,
          });
          showErrorToast("Kunne ikke åbne bekendtgørelsen.");
          return;
        }
        const useHtml =
          legislation.htmlAvailable || legislation.htmlProcessingIncomplete;
        openAppNavigation({
          target: useHtml
            ? withDocSearch(
                resolveSearchLegislationOpenNavigationTarget(
                  legislation,
                  consolidationFeatureEnabled
                ),
                docSearch
              )
            : {
                kind: "legislationPdf",
                documentId: legislation.id,
                source: legislation.source,
                context: "app",
              },
          navigate,
          event,
        });
      } else if (result.workspace) {
        openAppNavigation({
          target: {
            kind: "workspaceFiles",
            workspaceId: result.workspace.id,
          },
          navigate,
          event,
        });
      } else if (result.workspaceDocument) {
        const wd = result.workspaceDocument;
        const documentRouteId =
          wd.id?.trim() ? wd.id : wd.blobId?.trim() ?? "";
        if (!documentRouteId) {
          logger.warn("Workspace document search result has no id", {
            operation: "openSearchResult",
            workspaceId: wd.workspaceId,
          });
          showErrorToast("Dokumentet kunne ikke åbnes.");
          return;
        }
        openAppNavigation({
          target: {
            kind: "workspaceDocument",
            workspaceId: wd.workspaceId,
            documentId: documentRouteId,
            docSearch,
          },
          navigate,
          event,
          onWorkspaceDocumentNavigate: resetWorkspaceDocumentVersionStore,
        });
      } else if (result.chat) {
        const openingNewTab = shouldOpenInNewTab(event);
        openAppNavigation({
          target: { kind: "chatConversation" },
          navigate,
          event,
        });
        if (!openingNewTab) {
          selectConversationById(result.chat.id);
          scrollToConversation(result.chat.id);
        }
      }

      await handleCreateSearch();
    },
    [
      navigate,
      selectConversationById,
      handleCreateSearch,
      setIsSearchbarOpen,
      resetWorkspaceDocumentVersionStore,
      consolidationFeatureEnabled,
      searchQuery?.searchTerm,
      searchQuery?.searchTags,
    ]
  );

  return {
    handleResultClick,
  };
};
